import { API_URL, api } from "../api/client";
import { DiaryEntry } from "../types/Diary";
import {
  FeedResponse,
  LikeState,
  PartReviews,
  PersonSummary,
  Profile,
  ReviewComment,
  SharedPartReview,
  Take,
  WorkReviews,
} from "../types/Social";
import Stats from "../types/Stats";
import { Topic } from "./changes";
import { useApi } from "./useApi";
import { usePagedApi } from "./usePagedApi";
import { useWholeApi } from "./useWholeApi";

// Social reads and writes: follows, likes, comments, reports and blocks.
// None waits offline (plan 019 §3.4): others see them, and they mean
// nothing hours late.

const user = (username: string) => `/users/${encodeURIComponent(username)}`;

// Where an avatar's image is, from the path the API gives.
export const avatarSrc = (path: string) => `${API_URL}${path}`;

export const followUser = (username: string, follow: boolean) => {
  const path = `${user(username)}/follow`;
  return follow
    ? api.post<{ isFollowing: boolean }>(path, {})
    : api.del<{ isFollowing: boolean }>(path);
};

export const blockUser = (username: string, block: boolean) => {
  const path = `${user(username)}/block`;
  return block ? api.post(path, {}) : api.del(path);
};

// A part review's likes are under its own id (ADR 0030).
export const likeReview = (entryId: number, like: boolean, part: boolean) => {
  const path = part
    ? `/library/part-reviews/${entryId}/like`
    : `/library/${entryId}/like`;
  return like ? api.post<LikeState>(path, {}) : api.del<LikeState>(path);
};

export const report = (about: {
  username?: string;
  trackingId?: number;
  partReviewId?: number;
  commentId?: number;
  reason: string;
}) => api.post("/reports", about);

// A review's comments, and what each change leaves of them.
export const commentsPath = (entryId: number) => `/library/${entryId}/comments`;

export const postComment = (entryId: number, body: string, parentId?: number) =>
  api.post<ReviewComment[]>(
    commentsPath(entryId),
    parentId ? { body, parentId } : { body },
  );

export const editComment = (id: number, body: string) =>
  api.patch<ReviewComment[]>(`/library/comments/${id}`, { body });

export const deleteComment = (id: number) => api.del(`/library/comments/${id}`);

// The next page of a profile's shelf, of a work's reviews, or of the feed.
export const moreProfile = (username: string, offset: number, limit: number) =>
  api.get<Profile>(`${user(username)}?limit=${limit}&offset=${offset}`);

// A season's or an episode's reviews are named by `partQuery` (ADR 0030).
export const moreReviews = <R = WorkReviews>(
  workId: number,
  offset: number,
  partQuery?: string,
) =>
  api.get<R>(
    `/works/${workId}/reviews?${partQuery ? `${partQuery}&` : ""}offset=${offset}`,
  );

export const moreFeed = (before: string) =>
  api.get<FeedResponse>(
    `/feed?parts=1&imports=1&goals=1&before=${encodeURIComponent(before)}`,
  );

// Social reads, for a page to show (ADR 0055). Each takes a username as
// typed and encodes it; those that can show your shelf (your profile, diary,
// stats, take) reload when it changes.
const SHELF: Topic[] = ["shelf"];

// A profile, with its first `limit` of recent activity.
export const useProfile = (username: string, limit: number) =>
  useApi<Profile>(`${user(username)}?limit=${limit}`, { about: SHELF });

// Someone's stats over all time, or for a year (null reads nothing).
export const useStats = (username: string) =>
  useApi<Stats>(`${user(username)}/stats`, { about: SHELF });
export const useYearStats = (username: string, year: number | null) =>
  useApi<Stats>(year === null ? null : `${user(username)}/stats?year=${year}`, {
    about: SHELF,
  });

// Who someone follows, or who follows them.
export const useFollows = (
  username: string,
  which: "followers" | "following",
) => useApi<PersonSummary[]>(`${user(username)}/${which}`);

// Someone's take on a work: their entry and its log.
export const useTake = (username: string, workId: string) =>
  useApi<Take>(`${user(username)}/works/${encodeURIComponent(workId)}`, {
    about: SHELF,
  });

// The comments under a review, signed in or not (`auth`).
export const useComments = (entryId: number, auth: boolean) =>
  useApi<ReviewComment[]>(commentsPath(entryId), { auth });

// What the people you follow did lately, with part and import reviews
// and goals reached.
export const useFeed = () =>
  useApi<FeedResponse>("/feed?parts=1&imports=1&goals=1");

// Someone's diary for a year and type, in the viewer's time zone (`tz`):
// a page at a time while `paged`, else whole (a year's months and counts).
export const useDiary = (
  username: string,
  filters: { year: number | null; type: string | null; tz: string },
  paged: boolean,
  pageSize: number,
) => {
  const search = new URLSearchParams();
  if (filters.year !== null) search.set("year", String(filters.year));
  if (filters.type !== null) search.set("type", filters.type);
  search.set("tz", filters.tz);
  const path = `${user(username)}/diary?${search}`;
  return {
    pages: usePagedApi<DiaryEntry>(paged ? path : null, {
      pageSize,
      keyOf: (entry) => entry.logId,
      about: SHELF,
    }),
    whole: useWholeApi<DiaryEntry>(paged ? null : path, SHELF),
  };
};

// The last four of someone's diary (of a type); off reads nothing.
export const useRecentDiary = (
  username: string,
  type: string | null,
  on: boolean,
) =>
  useApi<DiaryEntry[]>(
    on ? `${user(username)}/diary?limit=4${type ? `&type=${type}` : ""}` : null,
    { about: SHELF },
  );

// People by name: a few as you type (`q` empty reads nothing), or Browse's,
// a page at a time (null reads nothing; empty, everyone).
export const usePeopleSearch = (q: string, limit: number) =>
  useApi<PersonSummary[]>(
    q ? `/users?${new URLSearchParams({ q, limit: String(limit) })}` : null,
    { debounce: 200 },
  );
export const usePeople = (q: string | null, pageSize: number) =>
  usePagedApi<PersonSummary>(
    q === null ? null : q ? `/users?${new URLSearchParams({ q })}` : "/users",
    { pageSize, keyOf: (person) => person.username, debounce: 300 },
  );

// A work's reviews; a part's (`partQuery`, ADR 0030); and the best of its
// parts'.
export const useWorkReviews = (workId: number | string) =>
  useApi<WorkReviews>(`/works/${workId}/reviews`);
export const usePartReviews = (workId: number, partQuery: string) =>
  useApi<PartReviews>(`/works/${workId}/reviews?${partQuery}`);
export const useBestPartReviews = (workId: number) =>
  useApi<{ reviews: SharedPartReview[] }>(
    `/works/${workId}/reviews?parts=best`,
  );
